import React, { useEffect, useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { NotePencil, Trash, PlusCircle, Sparkle, MagnifyingGlass, ChartLineUp, Pulse, X, LockKey, DownloadSimple, ArrowsOut, Fire, FolderSimple, FolderSimplePlus, Plus, CloudSlash, ArrowClockwise, PencilSimple, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { haptic } from "@/lib/haptics";
import api from "@/lib/api";

const MOODS = ["🌑 Heavy", "🌗 Mixed", "🌕 Light", "⚡ Charged", "🌊 Calm"];
const TRIGGERS = ["Conflict", "Rejection", "Work", "Money", "Family", "Partner", "Loneliness", "Comparison", "Uncertainty", "Criticism"];
const BODY = ["Tight chest", "Shallow breath", "Tense shoulders", "Stomach knot", "Heavy", "Restless", "Calm", "Tired"];
const RELATIONSHIP = ["Partner", "Family", "Friend", "Work", "Self", "None"];
const PATTERN_TAGS = ["Approval-Seeking", "Avoidance", "Control", "Perfectionism", "Overthinking", "Self-Doubt", "Anxious Attachment", "Reactivity", "Suppression"];
const PROMPTS = [
  "When did I abandon myself today, and why?",
  "What did I feel but not say?",
  "Where did my pattern show up today?",
  "What would self-compassion sound like right now?",
];

const PENDING_KEY = "theinner_journal_pending";
const CACHE_KEY = "theinner_journal_cache";
const loadLS = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };
const saveLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* noop */ } };
const isNetworkError = (e) => !e?.response;

const Tag = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-full border px-3 py-1.5 text-xs transition-all active:scale-95 ${active ? "border-teal-400 bg-teal-500/15 text-teal-300" : "border-slate-800 text-slate-400 hover:border-slate-600"}`}>
    {children}
  </button>
);

const fmtDate = (d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [pending, setPending] = useState(() => loadLS(PENDING_KEY, []));
  const [folders, setFolders] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [mood, setMood] = useState(null);
  const [intensity, setIntensity] = useState(3);
  const [triggers, setTriggers] = useState([]);
  const [bodyState, setBodyState] = useState([]);
  const [relationship, setRelationship] = useState(null);
  const [patternTags, setPatternTags] = useState([]);
  const [composerFolder, setComposerFolder] = useState(null);
  const [reflection, setReflection] = useState(null);
  const [reflecting, setReflecting] = useState(false);
  const [search, setSearch] = useState("");
  const [activeFolder, setActiveFolder] = useState("all"); // "all" | "unfiled" | folder_id
  const [insights, setInsights] = useState(null);
  const [showInsights, setShowInsights] = useState(false);
  const [focus, setFocus] = useState(false);
  const [streak, setStreak] = useState(0);
  const [locked, setLocked] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [passInput, setPassInput] = useState("");
  const [showLockSettings, setShowLockSettings] = useState(false);
  const [newPass, setNewPass] = useState("");
  const [online, setOnline] = useState(navigator.onLine);
  const [readEntry, setReadEntry] = useState(null);
  const [showFolderMgr, setShowFolderMgr] = useState(false);
  const [newFolder, setNewFolder] = useState("");
  const [moveTarget, setMoveTarget] = useState(null); // entry being moved

  const loadFolders = useCallback(() => api.get("/journal/folders").then(({ data }) => setFolders(data.folders)).catch(() => {}), []);
  const load = useCallback(() =>
    api.get("/journal").then(({ data }) => { setEntries(data.entries); saveLS(CACHE_KEY, data.entries); })
      .catch(() => { setEntries(loadLS(CACHE_KEY, [])); }), []);

  const flushQueue = useCallback(async () => {
    const q = loadLS(PENDING_KEY, []);
    if (q.length === 0) return;
    const remaining = [];
    for (const p of q) {
      try {
        const { _pending, entry_id, created_at, ...payload } = p;
        await api.post("/journal", payload);
      } catch (e) {
        remaining.push(p);
      }
    }
    saveLS(PENDING_KEY, remaining);
    setPending(remaining);
    if (remaining.length < q.length) { await load(); await loadFolders(); toast.success("Journal synced."); }
  }, [load, loadFolders]);

  useEffect(() => {
    api.get("/journal/lock-status").then((r) => setLocked(r.data.locked)).catch(() => {});
    api.get("/journal/streak").then((r) => setStreak(r.data.streak)).catch(() => {});
    loadFolders();
    load().then(flushQueue);
  }, [load, loadFolders, flushQueue]);

  useEffect(() => {
    const goOnline = () => { setOnline(true); flushQueue(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, [flushQueue]);

  const unlock = async () => {
    const { data } = await api.post("/journal/passcode/verify", { passcode: passInput });
    if (data.ok) { setUnlocked(true); setPassInput(""); } else toast.error("Incorrect passcode.");
  };
  const savePasscode = async () => {
    if (newPass.length < 4) { toast.error("Passcode must be at least 4 characters."); return; }
    await api.post("/journal/passcode", { passcode: newPass });
    setLocked(true); setUnlocked(true); setNewPass(""); setShowLockSettings(false);
    toast.success("Journal locked. You'll need this passcode next time.");
  };
  const removePasscode = async () => {
    try { await api.delete("/journal/passcode", { data: { passcode: passInput || newPass } }); setLocked(false); setShowLockSettings(false); setPassInput(""); setNewPass(""); toast.success("Lock removed."); }
    catch { toast.error("Incorrect passcode."); }
  };
  const exportJournal = async () => {
    const { data } = await api.get("/journal/export");
    const blob = new Blob([data.markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "theinner-journal.md"; a.click();
    toast.success(`Exported ${data.count} entries.`);
  };

  const toggle = (val, list, setter) => setter(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);

  const resetComposer = () => {
    setTitle(""); setContent(""); setMood(null); setIntensity(3); setTriggers([]); setBodyState([]);
    setRelationship(null); setPatternTags([]); setReflection(null); setInsights(null); setFocus(false);
  };

  const save = async () => {
    if (!content.trim()) return;
    haptic(12);
    const folderTarget = composerFolder || (activeFolder !== "all" && activeFolder !== "unfiled" ? activeFolder : null);
    const payload = { title, content, mood, intensity, triggers, body_state: bodyState, relationship_context: relationship, pattern_tags: patternTags, folder_id: folderTarget };
    try {
      await api.post("/journal", payload);
      resetComposer();
      api.get("/journal/streak").then((r) => setStreak(r.data.streak)).catch(() => {});
      toast.success("Entry saved. This feeds your living profile.");
      await load(); await loadFolders();
    } catch (e) {
      if (isNetworkError(e) || !navigator.onLine) {
        const local = { ...payload, entry_id: `pending_${Date.now()}`, _pending: true, created_at: new Date().toISOString(), triggers, body_state: bodyState, pattern_tags: patternTags };
        const q = [local, ...loadLS(PENDING_KEY, [])];
        saveLS(PENDING_KEY, q); setPending(q);
        resetComposer();
        toast.message("Saved offline — will sync when you're back online.");
      } else {
        toast.error("Couldn't save. Please try again.");
      }
    }
  };

  const reflect = async () => {
    if (!content.trim()) return;
    setReflecting(true); setReflection(null);
    try { const { data } = await api.post("/journal/reflect", { content }); setReflection(data.reflection); }
    catch { toast.error("Couldn't reflect right now."); }
    finally { setReflecting(false); }
  };

  const remove = async (id) => {
    if (String(id).startsWith("pending_")) {
      const q = loadLS(PENDING_KEY, []).filter((p) => p.entry_id !== id);
      saveLS(PENDING_KEY, q); setPending(q); setReadEntry(null); return;
    }
    await api.delete(`/journal/${id}`); setReadEntry(null); load(); loadFolders();
  };

  const moveEntry = async (id, folderId) => {
    try {
      await api.put(`/journal/${id}/folder?folder_id=${folderId || ""}`);
      setMoveTarget(null); setReadEntry(null);
      toast.success(folderId ? "Moved to folder." : "Moved to Unfiled.");
      await load(); await loadFolders();
    } catch { toast.error("Couldn't move entry."); }
  };

  const createFolder = async () => {
    const name = newFolder.trim();
    if (!name) return;
    try { await api.post("/journal/folders", { name }); setNewFolder(""); await loadFolders(); toast.success(`Folder "${name}" created.`); }
    catch { toast.error("Couldn't create folder."); }
  };
  const renameFolder = async (id, name) => { if (!name.trim()) return; await api.put(`/journal/folders/${id}`, { name: name.trim() }); loadFolders(); };
  const deleteFolder = async (id) => {
    await api.delete(`/journal/folders/${id}`);
    if (activeFolder === id) setActiveFolder("all");
    loadFolders(); load(); toast.success("Folder deleted. Its entries are now Unfiled.");
  };

  const openInsights = async () => {
    setShowInsights(true);
    if (!insights) { try { const { data } = await api.get("/journal/insights"); setInsights(data); } catch { /* offline */ } }
  };

  const allEntries = useMemo(() => [...pending, ...entries], [pending, entries]);
  const folderMap = useMemo(() => Object.fromEntries(folders.map((f) => [f.folder_id, f])), [folders]);

  const filtered = useMemo(() => allEntries.filter((e) => {
    const matchSearch = !search || (e.content || "").toLowerCase().includes(search.toLowerCase()) || (e.title || "").toLowerCase().includes(search.toLowerCase());
    const matchFolder = activeFolder === "all" ? true : activeFolder === "unfiled" ? !e.folder_id : e.folder_id === activeFolder;
    return matchSearch && matchFolder;
  }), [allEntries, search, activeFolder]);

  if (locked && !unlocked) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center" data-testid="journal-lock-gate">
        <LockKey size={40} weight="duotone" className="text-teal-400" />
        <h2 className="mt-4 font-heading text-2xl font-light text-slate-100">Your journal is locked</h2>
        <p className="mt-2 text-sm text-slate-400">Enter your passcode to continue.</p>
        <div className="mt-5 flex w-full max-w-xs flex-col gap-2 sm:flex-row">
          <input type="password" value={passInput} onChange={(e) => setPassInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && unlock()} placeholder="Passcode" className="flex-1 rounded-full border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-slate-200 outline-none focus:border-teal-500" data-testid="journal-passcode-input" autoFocus />
          <button onClick={unlock} className="rounded-full bg-teal-500 px-5 py-3 text-sm font-semibold text-slate-950 hover:bg-teal-400" data-testid="journal-unlock-btn">Unlock</button>
        </div>
      </div>
    );
  }

  const folderChip = (key, label, count, color) => (
    <button key={key} onClick={() => { haptic(8); setActiveFolder(key); }} data-testid={`folder-chip-${key}`}
      className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-2 text-sm transition-all active:scale-95 ${activeFolder === key ? "border-teal-400 bg-teal-500/15 text-teal-200" : "border-slate-800 bg-slate-900 text-slate-400"}`}>
      {color && <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />}
      {label}
      {typeof count === "number" && <span className="rounded-full bg-slate-800 px-1.5 text-[11px] text-slate-500">{count}</span>}
    </button>
  );

  return (
    <div data-testid="journal-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">Journal</p>
          <h1 className="mt-2 font-heading text-2xl font-light tracking-tight text-slate-50 sm:text-3xl">A calm place to understand yourself</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {streak > 0 && <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-3 py-1 text-xs text-amber-300" data-testid="journal-streak"><Fire size={13} weight="fill" /> {streak}-day streak</span>}
            {!online && <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 px-3 py-1 text-xs text-slate-400" data-testid="journal-offline"><CloudSlash size={13} /> Offline</span>}
            {pending.length > 0 && <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-500/10 px-3 py-1 text-xs text-sky-300" data-testid="journal-pending"><ArrowClockwise size={13} className={online ? "animate-spin" : ""} /> {pending.length} to sync</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setFocus(true)} className="inline-flex items-center gap-1.5 rounded-full border border-slate-800 px-3 py-2 text-sm text-slate-300 hover:border-teal-500/40 hover:text-teal-300" data-testid="journal-focus-btn"><ArrowsOut size={15} /> <span className="hidden sm:inline">Focus</span></button>
          <button onClick={exportJournal} className="inline-flex items-center gap-1.5 rounded-full border border-slate-800 px-3 py-2 text-sm text-slate-300 hover:border-teal-500/40 hover:text-teal-300" data-testid="journal-export-btn"><DownloadSimple size={15} /> <span className="hidden sm:inline">Export</span></button>
          <button onClick={() => setShowLockSettings(true)} className="inline-flex items-center gap-1.5 rounded-full border border-slate-800 px-3 py-2 text-sm text-slate-300 hover:border-teal-500/40 hover:text-teal-300" data-testid="journal-lock-btn"><LockKey size={15} /> <span className="hidden sm:inline">Lock</span></button>
          <button onClick={openInsights} className="inline-flex items-center gap-1.5 rounded-full border border-slate-800 px-3 py-2 text-sm text-slate-300 hover:border-teal-500/40 hover:text-teal-300" data-testid="journal-insights-btn"><ChartLineUp size={15} weight="duotone" /> <span className="hidden sm:inline">Insights</span></button>
        </div>
      </div>

      {/* Focus mode overlay */}
      {focus && (
        <div className="fixed inset-0 z-50 flex flex-col bg-slate-950 p-6 pt-[calc(1.5rem+env(safe-area-inset-top))] sm:p-12" data-testid="journal-focus-mode">
          <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col">
            <div className="flex items-center justify-between">
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="bg-transparent font-heading text-2xl font-light text-slate-100 placeholder-slate-700 outline-none" />
              <button onClick={() => setFocus(false)} className="text-slate-500 hover:text-slate-200"><X size={22} /></button>
            </div>
            <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Just write. Nothing else." autoFocus className="mt-4 flex-1 resize-none bg-transparent text-lg leading-relaxed text-slate-200 placeholder-slate-700 outline-none" data-testid="journal-focus-textarea" />
            <button onClick={save} disabled={!content.trim()} className="mt-4 self-end rounded-full bg-teal-500 px-6 py-3 text-sm font-semibold text-slate-950 hover:bg-teal-400 disabled:opacity-40">Save &amp; exit</button>
          </div>
        </div>
      )}

      {/* Lock settings modal */}
      {showLockSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowLockSettings(false)} data-testid="journal-lock-settings">
          <div className="w-full max-w-sm rounded-2xl border border-slate-800 bg-slate-900 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-medium text-slate-100">{locked ? "Manage journal lock" : "Lock your journal"}</h3>
            <p className="mt-2 text-sm text-slate-400">{locked ? "Enter your current passcode to remove the lock, or set a new one." : "Set a passcode (min 4 chars). You'll need it each time you open your journal."}</p>
            <input type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder={locked ? "Passcode" : "New passcode"} className="mt-4 w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-slate-200 outline-none focus:border-teal-500" data-testid="journal-new-passcode" />
            <div className="mt-4 flex gap-2">
              <button onClick={savePasscode} className="flex-1 rounded-full bg-teal-500 px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-teal-400" data-testid="journal-save-passcode">{locked ? "Update lock" : "Set lock"}</button>
              {locked && <button onClick={() => { setPassInput(newPass); removePasscode(); }} className="rounded-full border border-red-500/30 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10">Remove</button>}
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        {/* Composer */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 sm:p-6 lg:col-span-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="mb-3 w-full bg-transparent font-heading text-lg font-light text-slate-100 placeholder-slate-600 outline-none" data-testid="journal-title" />
          <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="What's alive in you right now?" rows={6}
            className="w-full resize-none rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 text-base leading-relaxed text-slate-200 placeholder-slate-600 outline-none focus:border-teal-500/60 focus:ring-2 focus:ring-teal-500/20" data-testid="journal-textarea" />

          {/* Save-to folder */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wider text-slate-500"><FolderSimple size={13} className="mr-1 inline" />Save to</span>
            <button onClick={() => setComposerFolder(null)} className={`rounded-full border px-3 py-1 text-xs ${!composerFolder ? "border-teal-400 bg-teal-500/15 text-teal-300" : "border-slate-800 text-slate-400"}`} data-testid="composer-folder-none">Unfiled</button>
            {folders.map((f) => (
              <button key={f.folder_id} onClick={() => setComposerFolder(f.folder_id)} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${composerFolder === f.folder_id ? "border-teal-400 bg-teal-500/15 text-teal-300" : "border-slate-800 text-slate-400"}`} data-testid={`composer-folder-${f.folder_id}`}>
                <span className="h-2 w-2 rounded-full" style={{ background: f.color }} />{f.name}
              </button>
            ))}
            <button onClick={() => setShowFolderMgr(true)} className="rounded-full border border-dashed border-slate-700 px-2.5 py-1 text-xs text-slate-400 hover:border-teal-500/40 hover:text-teal-300" data-testid="composer-new-folder"><Plus size={12} className="inline" /> Folder</button>
          </div>

          <p className="mt-4 text-xs font-medium uppercase tracking-wider text-slate-500">How does it feel?</p>
          <div className="mt-2 flex flex-wrap gap-2">{MOODS.map((m) => <Tag key={m} active={mood === m} onClick={() => setMood(mood === m ? null : m)} testid={`journal-mood-${m}`}>{m}</Tag>)}</div>

          <div className="mt-4 flex items-center gap-3">
            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Intensity</span>
            <input type="range" min="1" max="5" value={intensity} onChange={(e) => setIntensity(Number(e.target.value))} className="flex-1 accent-teal-500" data-testid="journal-intensity" />
            <span className="w-4 text-center font-mono text-sm text-teal-300">{intensity}</span>
          </div>

          <p className="mt-4 text-xs font-medium uppercase tracking-wider text-slate-500">Triggers</p>
          <div className="mt-2 flex flex-wrap gap-2">{TRIGGERS.map((t) => <Tag key={t} active={triggers.includes(t)} onClick={() => toggle(t, triggers, setTriggers)} testid={`journal-trigger-${t}`}>{t}</Tag>)}</div>

          <p className="mt-4 text-xs font-medium uppercase tracking-wider text-slate-500">In your body</p>
          <div className="mt-2 flex flex-wrap gap-2">{BODY.map((t) => <Tag key={t} active={bodyState.includes(t)} onClick={() => toggle(t, bodyState, setBodyState)} testid={`journal-body-${t}`}>{t}</Tag>)}</div>

          <p className="mt-4 text-xs font-medium uppercase tracking-wider text-slate-500">Which pattern showed up?</p>
          <div className="mt-2 flex flex-wrap gap-2">{PATTERN_TAGS.map((t) => <Tag key={t} active={patternTags.includes(t)} onClick={() => toggle(t, patternTags, setPatternTags)} testid={`journal-pattern-${t}`}>{t}</Tag>)}</div>

          <p className="mt-4 text-xs font-medium uppercase tracking-wider text-slate-500">Who was it about?</p>
          <div className="mt-2 flex flex-wrap gap-2">{RELATIONSHIP.map((t) => <Tag key={t} active={relationship === t} onClick={() => setRelationship(relationship === t ? null : t)} testid={`journal-rel-${t}`}>{t}</Tag>)}</div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button onClick={save} disabled={!content.trim()} className="inline-flex items-center gap-2 rounded-full bg-teal-500 px-5 py-3 text-sm font-semibold text-slate-950 transition-all hover:bg-teal-400 active:scale-95 disabled:opacity-40" data-testid="journal-save-btn"><PlusCircle size={16} weight="fill" /> Save entry</button>
            <button onClick={reflect} disabled={!content.trim() || reflecting} className="inline-flex items-center gap-2 rounded-full border border-indigo-500/40 px-5 py-3 text-sm font-medium text-indigo-300 hover:bg-indigo-500/10 disabled:opacity-40" data-testid="journal-reflect-btn"><Sparkle size={15} weight={reflecting ? "regular" : "fill"} className={reflecting ? "animate-pulse" : ""} /> {reflecting ? "Reflecting…" : "Reflect with AI"}</button>
          </div>

          {reflection && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-4 text-sm leading-relaxed text-slate-300" data-testid="journal-reflection">
              <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-indigo-300"><Sparkle size={12} weight="fill" /> A reflection</span>
              {reflection}
            </motion.div>
          )}

          <div className="mt-6 border-t border-slate-800 pt-4">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Prompts</p>
            <div className="mt-2 flex flex-col gap-1.5">{PROMPTS.map((p) => <button key={p} onClick={() => setContent((c) => (c ? c + "\n\n" : "") + p + "\n")} className="text-left text-sm text-slate-400 hover:text-teal-300">• {p}</button>)}</div>
          </div>
        </div>

        {/* Entries + folders */}
        <div className="lg:col-span-2">
          {/* Folder rail */}
          <div className="flex items-center gap-2">
            <div className="-mx-1 flex flex-1 gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="folder-rail">
              {folderChip("all", "All", allEntries.length)}
              {folderChip("unfiled", "Unfiled", allEntries.filter((e) => !e.folder_id).length)}
              {folders.map((f) => folderChip(f.folder_id, f.name, f.count, f.color))}
            </div>
            <button onClick={() => setShowFolderMgr(true)} className="shrink-0 rounded-full border border-slate-800 p-2 text-slate-400 hover:border-teal-500/40 hover:text-teal-300" data-testid="manage-folders-btn" aria-label="Manage folders"><FolderSimplePlus size={16} /></button>
          </div>

          {/* Search */}
          <div className="relative mt-3">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search entries…" className="w-full rounded-full border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-3 text-sm text-slate-200 placeholder-slate-600 outline-none focus:border-teal-500" data-testid="journal-search" />
          </div>

          <div className="mt-4 space-y-3" data-testid="journal-list">
            {filtered.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-800 p-10 text-center">
                <NotePencil size={28} weight="duotone" className="mx-auto text-slate-600" />
                <p className="mt-3 text-sm text-slate-500">{allEntries.length === 0 ? "No entries yet. Start with a prompt." : "Nothing here yet."}</p>
              </div>
            )}
            {filtered.map((e) => {
              const f = e.folder_id ? folderMap[e.folder_id] : null;
              return (
                <motion.div key={e.entry_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="group rounded-xl border border-slate-800 bg-slate-900 p-4" data-testid="journal-entry">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span>{fmtDate(e.created_at)}</span>
                      {f && <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5" style={{ background: `${f.color}1a`, color: f.color }}><FolderSimple size={10} weight="fill" />{f.name}</span>}
                      {e.mood && <span className="rounded-full bg-slate-800 px-2 py-0.5">{e.mood}</span>}
                      {e._pending && <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-2 py-0.5 text-sky-300"><ArrowClockwise size={10} /> pending</span>}
                    </div>
                    <button onClick={() => remove(e.entry_id)} className="shrink-0 text-slate-600 opacity-60 transition-opacity hover:text-red-400 group-hover:opacity-100" data-testid="journal-delete-btn"><Trash size={15} /></button>
                  </div>
                  {e.title && <p className="mt-2 font-heading text-base font-medium text-slate-100">{e.title}</p>}
                  <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-300 [display:-webkit-box] [overflow:hidden] [-webkit-box-orient:vertical] [-webkit-line-clamp:3]">{e.content}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <button onClick={() => { haptic(6); setReadEntry(e); }} className="inline-flex items-center gap-1 text-xs font-medium text-teal-400 hover:text-teal-300" data-testid="journal-readfull-btn">Read full <CaretRight size={11} weight="bold" /></button>
                    {!e._pending && (
                      <button onClick={() => setMoveTarget(e)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300" data-testid="journal-move-btn"><FolderSimple size={12} /> Move</button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Read full modal */}
      {readEntry && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 sm:items-center" onClick={() => setReadEntry(null)} data-testid="journal-read-modal">
          <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="max-h-[85vh] w-full overflow-y-auto rounded-t-3xl border border-slate-800 bg-slate-900 p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:max-w-lg sm:rounded-3xl" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs text-slate-500">{fmtDate(readEntry.created_at)}</p>
                {readEntry.title && <h3 className="mt-1 font-heading text-xl font-medium text-slate-100">{readEntry.title}</h3>}
              </div>
              <button onClick={() => setReadEntry(null)} className="text-slate-500 hover:text-slate-200"><X size={20} /></button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              {readEntry.mood && <span className="rounded-full bg-slate-800 px-2 py-0.5 text-slate-300">{readEntry.mood}</span>}
              {typeof readEntry.intensity === "number" && <span className="rounded-full bg-slate-800 px-2 py-0.5 text-slate-300">⚡ {readEntry.intensity}</span>}
              {readEntry.folder_id && folderMap[readEntry.folder_id] && <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5" style={{ background: `${folderMap[readEntry.folder_id].color}1a`, color: folderMap[readEntry.folder_id].color }}><FolderSimple size={10} weight="fill" />{folderMap[readEntry.folder_id].name}</span>}
            </div>
            <p className="mt-4 whitespace-pre-wrap text-[15px] leading-relaxed text-slate-200">{readEntry.content}</p>
            {(readEntry.pattern_tags?.length > 0 || readEntry.triggers?.length > 0) && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {(readEntry.pattern_tags || []).map((t) => <span key={t} className="rounded-full bg-teal-500/10 px-2 py-0.5 text-[11px] text-teal-300">{t}</span>)}
                {(readEntry.triggers || []).map((t) => <span key={t} className="rounded-full bg-slate-800 px-2 py-0.5 text-[11px] text-slate-400">{t}</span>)}
              </div>
            )}
            <div className="mt-6 flex items-center justify-between border-t border-slate-800 pt-4">
              {!readEntry._pending
                ? <button onClick={() => { setMoveTarget(readEntry); }} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:border-teal-500/40" data-testid="read-move-btn"><FolderSimple size={14} /> Move to folder</button>
                : <span className="text-xs text-sky-300">Will sync when online</span>}
              <button onClick={() => remove(readEntry.entry_id)} className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10" data-testid="read-delete-btn"><Trash size={14} /> Delete</button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Move-to-folder picker */}
      {moveTarget && (
        <div className="fixed inset-0 z-[55] flex items-end justify-center bg-black/70 sm:items-center" onClick={() => setMoveTarget(null)} data-testid="journal-move-modal">
          <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full rounded-t-3xl border border-slate-800 bg-slate-900 p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:max-w-sm sm:rounded-3xl" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between"><h3 className="font-heading text-lg font-medium text-slate-100">Move to folder</h3><button onClick={() => setMoveTarget(null)} className="text-slate-500"><X size={20} /></button></div>
            <div className="mt-4 space-y-2">
              <button onClick={() => moveEntry(moveTarget.entry_id, "")} className="flex w-full items-center gap-2 rounded-xl border border-slate-800 px-4 py-3 text-left text-sm text-slate-300 hover:border-teal-500/40" data-testid="move-to-unfiled">Unfiled</button>
              {folders.map((f) => (
                <button key={f.folder_id} onClick={() => moveEntry(moveTarget.entry_id, f.folder_id)} className="flex w-full items-center gap-2 rounded-xl border border-slate-800 px-4 py-3 text-left text-sm text-slate-300 hover:border-teal-500/40" data-testid={`move-to-${f.folder_id}`}>
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: f.color }} />{f.name}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      )}

      {/* Folder manager */}
      {showFolderMgr && (
        <div className="fixed inset-0 z-[55] flex items-end justify-center bg-black/70 sm:items-center" onClick={() => setShowFolderMgr(false)} data-testid="folder-manager">
          <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full rounded-t-3xl border border-slate-800 bg-slate-900 p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:max-w-md sm:rounded-3xl" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between"><h3 className="font-heading text-lg font-medium text-slate-100">Folders</h3><button onClick={() => setShowFolderMgr(false)} className="text-slate-500"><X size={20} /></button></div>
            <div className="mt-4 flex gap-2">
              <input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createFolder()} placeholder="New folder name (e.g. Self growth)" className="flex-1 rounded-full border border-slate-800 bg-slate-950 px-4 py-2.5 text-sm text-slate-200 outline-none focus:border-teal-500" data-testid="new-folder-input" />
              <button onClick={createFolder} className="rounded-full bg-teal-500 px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-teal-400" data-testid="create-folder-btn">Create</button>
            </div>
            <div className="mt-4 space-y-2">
              {folders.length === 0 && <p className="py-4 text-center text-sm text-slate-500">No folders yet. Create one to organise your journal.</p>}
              {folders.map((f) => (
                <FolderRow key={f.folder_id} folder={f} onRename={renameFolder} onDelete={deleteFolder} />
              ))}
            </div>
          </motion.div>
        </div>
      )}

      {/* Insights drawer */}
      {showInsights && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={() => setShowInsights(false)} data-testid="journal-insights-drawer">
          <motion.div initial={{ x: 400 }} animate={{ x: 0 }} className="h-full w-full max-w-md overflow-y-auto bg-slate-950 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-xl font-light text-slate-50"><Pulse size={20} weight="duotone" className="text-teal-400" /> Your patterns</h2>
              <button onClick={() => setShowInsights(false)} className="text-slate-500 hover:text-slate-200"><X size={20} /></button>
            </div>
            {!insights ? <div className="mt-10 flex justify-center"><div className="h-7 w-7 animate-spin rounded-full border-2 border-slate-800 border-t-teal-400" /></div>
              : !insights.has_data ? <p className="mt-8 text-sm text-slate-500">Write a few entries and your emotional patterns will appear here.</p>
              : (
                <div className="mt-6 space-y-6">
                  {insights.summary && <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-4"><p className="text-xs font-bold uppercase tracking-wider text-indigo-300">This week, in your words</p><p className="mt-2 text-sm leading-relaxed text-slate-300">{insights.summary}</p></div>}
                  {insights.recurring && <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200" data-testid="journal-recurring">{insights.recurring}</div>}
                  <div className="grid grid-cols-2 gap-3 text-center">
                    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4"><p className="font-heading text-2xl text-teal-300">{insights.total}</p><p className="text-xs text-slate-500">Entries</p></div>
                    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4"><p className="font-heading text-2xl text-teal-300">{insights.avg_intensity ?? "—"}</p><p className="text-xs text-slate-500">Avg intensity</p></div>
                  </div>
                  {[["Most common moods", insights.moods], ["Recurring triggers", insights.triggers], ["Patterns surfacing", insights.pattern_tags], ["Body signals", insights.body_states]].map(([title, arr]) => arr?.length > 0 && (
                    <div key={title}><p className="text-xs font-bold uppercase tracking-wider text-slate-500">{title}</p>
                      <div className="mt-2 flex flex-wrap gap-2">{arr.map((x) => <span key={x.name} className="rounded-full border border-slate-800 bg-slate-900 px-3 py-1 text-sm text-slate-300">{x.name} <span className="font-mono text-xs text-slate-500">{x.count}</span></span>)}</div>
                    </div>
                  ))}
                </div>
              )}
          </motion.div>
        </div>
      )}
    </div>
  );
}

function FolderRow({ folder, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(folder.name);
  return (
    <div className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950 px-3 py-2.5" data-testid={`folder-row-${folder.folder_id}`}>
      <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: folder.color }} />
      {editing ? (
        <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { onRename(folder.folder_id, name); setEditing(false); } }} className="flex-1 bg-transparent text-sm text-slate-200 outline-none" autoFocus />
      ) : (
        <span className="flex-1 truncate text-sm text-slate-200">{folder.name} <span className="text-xs text-slate-500">({folder.count})</span></span>
      )}
      {editing ? (
        <button onClick={() => { onRename(folder.folder_id, name); setEditing(false); }} className="text-xs font-semibold text-teal-400" data-testid={`folder-save-${folder.folder_id}`}>Save</button>
      ) : (
        <button onClick={() => setEditing(true)} className="text-slate-500 hover:text-teal-300" data-testid={`folder-rename-${folder.folder_id}`}><PencilSimple size={15} /></button>
      )}
      <button onClick={() => onDelete(folder.folder_id)} className="text-slate-600 hover:text-red-400" data-testid={`folder-delete-${folder.folder_id}`}><Trash size={15} /></button>
    </div>
  );
}
